import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { PaperclipIcon, SendIcon, CloseIcon } from '../Icons';
import styles from './CommentForm.module.scss';

export default function CommentForm({
  mode,
  text,
  onTextChange,
  maxLength,
  labels,
  allowAttachments,
  newFiles,
  onAddFiles,
  onRemoveNewFile,
  keptAttachments,
  onRemoveKeptAttachment,
  acceptedFileTypes,
  maxAttachments,
  submitting,
  error,
  onSubmit,
  onCancelEdit,
}) {
  const fileInputRef = useRef(null);

  const totalAttachments =
    (keptAttachments?.length || 0) + (newFiles?.length || 0);
  const atAttachmentLimit =
    Boolean(maxAttachments) && totalAttachments >= maxAttachments;
  const canSubmit =
    !submitting && text.trim().length > 0 && text.length <= maxLength;

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) onAddFiles(files);
    event.target.value = '';
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (canSubmit) onSubmit();
    }
  };

  return (
    <div className={styles.form}>
      <div className={styles.formHeader}>
        <span className={styles.formHeaderLabel}>
          {mode === 'edit' ? labels.editingTitle : labels.newCommentTitle}
          {`, ${text.trim().length}/${maxLength} ${labels.charsSuffix}`}
        </span>
        {mode === 'edit' && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={onCancelEdit}
            aria-label={labels.cancelEdit}
            title={labels.cancelEdit}
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {(keptAttachments?.length > 0 || newFiles?.length > 0) && (
        <ul className={styles.draftAttachmentList}>
          {keptAttachments?.map((attachment) => (
            <li
              key={`kept-${attachment.id}`}
              className={styles.draftAttachmentItem}
            >
              <PaperclipIcon />
              <span className={styles.draftAttachmentName}>
                {attachment.name}
              </span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => onRemoveKeptAttachment(attachment.id)}
                aria-label={labels.delete}
                title={labels.delete}
              >
                <CloseIcon />
              </button>
            </li>
          ))}
          {newFiles?.map((file, index) => (
            <li
              key={`new-${index}-${file.name}`}
              className={styles.draftAttachmentItem}
            >
              <PaperclipIcon />
              <span className={styles.draftAttachmentName}>{file.name}</span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => onRemoveNewFile(index)}
                aria-label={labels.delete}
                title={labels.delete}
              >
                <CloseIcon />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <div className={styles.formError}>{error}</div>}

      <div className={styles.inputRow}>
        {allowAttachments && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={acceptedFileTypes}
              className={styles.hiddenFileInput}
              onChange={handleFileChange}
            />
            <button
              type="button"
              className={styles.attachButton}
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting || atAttachmentLimit}
              aria-label={labels.attach}
              title={
                atAttachmentLimit
                  ? labels.attachmentsLimitReached(maxAttachments)
                  : labels.attach
              }
            >
              <PaperclipIcon />
            </button>
          </>
        )}

        <textarea
          className={styles.textarea}
          value={text}
          maxLength={maxLength}
          placeholder={labels.newCommentPlaceholder}
          onChange={(event) => onTextChange(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={submitting}
          rows={1}
          style={{
            resize: 'vertical',
            minHeight: 'auto',
          }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height =
              Math.min(e.target.scrollHeight, 3 * 24) + 'px';
          }}
        />

        <button
          type="button"
          className={styles.sendButton}
          onClick={onSubmit}
          disabled={!canSubmit}
          aria-label={mode === 'edit' ? labels.save : labels.send}
          title={mode === 'edit' ? labels.save : labels.send}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}

CommentForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  text: PropTypes.string.isRequired,
  onTextChange: PropTypes.func.isRequired,
  maxLength: PropTypes.number.isRequired,
  labels: PropTypes.object.isRequired,
  allowAttachments: PropTypes.bool,
  newFiles: PropTypes.arrayOf(
    PropTypes.instanceOf(typeof File !== 'undefined' ? File : Object)
  ),
  onAddFiles: PropTypes.func.isRequired,
  onRemoveNewFile: PropTypes.func.isRequired,
  keptAttachments: PropTypes.array,
  onRemoveKeptAttachment: PropTypes.func,
  acceptedFileTypes: PropTypes.string,
  maxAttachments: PropTypes.number,
  submitting: PropTypes.bool,
  error: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func,
};

CommentForm.defaultProps = {
  allowAttachments: true,
  newFiles: [],
  keptAttachments: [],
  submitting: false,
  error: '',
};
